import React, { useState, useEffect } from 'react';
import { createAssignment } from '../../utils/api/AssignmentsApi';

const COMPONENT_TYPES = [
  { value: 'document', label: 'Document', color: 'blue', icon: 'DOC' },
  { value: 'erd', label: 'ERD (new)', color: 'purple', icon: 'ERD' },
  { value: 'erd_env', label: 'ERD (Environment)', color: 'indigo', icon: 'ENV' },
  { value: 'code', label: 'Code', color: 'green', icon: 'CODE' }
];

const getTypeConfig = (type) => COMPONENT_TYPES.find(t => t.value === type) || COMPONENT_TYPES[0];

let componentCounter = 0;

const CreateAssignment = ({ classContext, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    title: '',
    assignment_type: 'document',
    description: '',
    due_date: '',
    lock_after_due_date: false,
    allow_late_submission: true,
    late_due_date: '',
    late_penalty_type: 'percentage',
    late_penalty_value: 10,
    late_penalty_interval: 'day',
    total_points: 100,
    is_published: false,
    environment_id: ''
  });
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [environments, setEnvironments] = useState([]);      // string[] for legacy selector
  const [fullEnvironments, setFullEnvironments] = useState([]); // {environment_id, name}[] for erd_env picker
  const [showAddMenu, setShowAddMenu] = useState(false);

  useEffect(() => {
    if (!classContext?.class_code) return;

    // Legacy environments list (string IDs)
    fetch('/api/environments', {
      headers: { 'Content-Type': 'application/json', 'Class-Code': classContext.class_code },
      credentials: 'include'
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.environments) {
          setEnvironments(data.environments.filter(e => e !== 'None'));
        }
      })
      .catch(() => {});

    // Full environment objects for erd_env component picker
    fetch(`/api/environments/full?class_code=${encodeURIComponent(classContext.class_code)}`, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.environments) setFullEnvironments(data.environments);
      })
      .catch(() => {});
  }, [classContext?.class_code]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // --- Component management ---
  const addComponent = (type) => {
    const typeConfig = getTypeConfig(type);
    const count = components.filter(c => c.type === type).length + 1;
    componentCounter++;
    const base = {
      _key: `comp_${componentCounter}`,
      type,
      title: `${typeConfig.label} ${count}`,
      points: 0,
      order: prev => prev.length
    };
    if (type === 'erd_env') base.environment_scope = 'all';
    setComponents(prev => [...prev, { ...base, order: prev.length }]);
    setShowAddMenu(false);
  };

  const removeComponent = (index) => {
    setComponents(prev => prev.filter((_, i) => i !== index).map((c, i) => ({ ...c, order: i })));
  };

  const updateComponent = (index, field, value) => {
    setComponents(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const moveComponent = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= components.length) return;
    setComponents(prev => {
      const arr = [...prev];
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return arr.map((c, i) => ({ ...c, order: i }));
    });
  };

  const componentPointsSum = components.reduce((sum, c) => sum + Number(c.points || 0), 0);
  const totalPoints = Number(formData.total_points || 0);
  const pointsMismatch = components.length > 0 && componentPointsSum !== totalPoints;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const dataToSubmit = {
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        late_due_date: formData.late_due_date ? new Date(formData.late_due_date).toISOString() : null,
        total_points: Number(formData.total_points),
        components: components.map(({ _key, ...rest }) => rest)
      };

      const result = await createAssignment(dataToSubmit, classContext);
      console.log('Assignment created:', result);
      onCreated && onCreated(result.assignment || result);
      onClose && onClose();
    } catch (err) {
      console.error('Error creating assignment:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]" onClick={onClose}>
      <div className="bg-white rounded-xl w-[90%] max-w-[800px] max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="m-0 text-2xl text-gray-800 font-semibold">Create New Assignment</h2>
          <button
            className="bg-transparent border-none text-3xl text-gray-400 cursor-pointer p-0 w-8 h-8 flex items-center justify-center rounded transition-all duration-200 hover:bg-gray-100 hover:text-gray-700"
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        {/* Form */}
        <form className="p-6" onSubmit={handleSubmit}>
          {/* Title Field */}
          <div className="mb-5">
            <label className="block mb-2 font-medium text-gray-800 text-sm" htmlFor="title">Title *</label>
            <input
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-colors duration-200 focus:outline-none focus:border-blue-600"
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              required
              placeholder="e.g., Database Design Project"
            />
          </div>

          {/* Description Field */}
          <div className="mb-5">
            <label className="block mb-2 font-medium text-gray-800 text-sm" htmlFor="description">Description</label>
            <textarea
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-colors duration-200 resize-y min-h-[80px] focus:outline-none focus:border-blue-600"
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe the assignment objectives and requirements..."
              rows={4}
            />
          </div>

          {/* Environment Selection */}
          {environments.length > 0 && (
            <div className="mb-5">
              <label className="block mb-2 font-medium text-gray-800 text-sm" htmlFor="environment_id">
                Link to Environment <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <select
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-colors duration-200 focus:outline-none focus:border-blue-600 bg-white"
                id="environment_id"
                value={formData.environment_id}
                onChange={(e) => handleChange('environment_id', e.target.value)}
              >
                <option value="">— No environment —</option>
                {environments.map(env => (
                  <option key={env} value={env}>{env}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Linking an environment attaches its requirements to this assignment's documents.
              </p>
            </div>
          )}

          {/* Assignment Components */}
          <div className="mb-5 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 text-sm m-0">Assignment Components</h3>
              <div className="relative">
                <button
                  type="button"
                  className="px-3 py-1.5 bg-blue-600 text-white border-none rounded-md text-xs font-medium cursor-pointer hover:bg-blue-700 transition-colors duration-200"
                  onClick={() => setShowAddMenu(!showAddMenu)}
                >
                  + Add Component
                </button>
                {showAddMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[160px]">
                    {COMPONENT_TYPES.map(ct => (
                      <button
                        key={ct.value}
                        type="button"
                        className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 border-none bg-transparent cursor-pointer flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg"
                        onClick={() => addComponent(ct.value)}
                      >
                        <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                          ct.color === 'blue'   ? 'bg-blue-100 text-blue-700' :
                          ct.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                          ct.color === 'indigo' ? 'bg-indigo-100 text-indigo-700' :
                          'bg-green-100 text-green-700'
                        }`}>{ct.icon}</span>
                        {ct.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {components.length === 0 ? (
              <p className="text-xs text-gray-500 m-0">
                No components added. A single document will be created by default.
              </p>
            ) : (
              <div className="space-y-2">
                {components.map((comp, idx) => {
                  const config = getTypeConfig(comp.type);
                  return (
                    <div key={comp._key} className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        {/* Reorder */}
                        <div className="flex flex-col gap-0.5">
                          <button
                            type="button"
                            className="p-0 border-none bg-transparent cursor-pointer text-gray-400 hover:text-gray-700 text-xs leading-none disabled:opacity-30 disabled:cursor-not-allowed"
                            onClick={() => moveComponent(idx, -1)}
                            disabled={idx === 0}
                            title="Move up"
                          >&#9650;</button>
                          <button
                            type="button"
                            className="p-0 border-none bg-transparent cursor-pointer text-gray-400 hover:text-gray-700 text-xs leading-none disabled:opacity-30 disabled:cursor-not-allowed"
                            onClick={() => moveComponent(idx, 1)}
                            disabled={idx === components.length - 1}
                            title="Move down"
                          >&#9660;</button>
                        </div>

                        {/* Type badge */}
                        <span className={`px-2 py-1 rounded text-xs font-bold whitespace-nowrap ${
                          config.color === 'blue'   ? 'bg-blue-100 text-blue-700' :
                          config.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                          config.color === 'indigo' ? 'bg-indigo-100 text-indigo-700' :
                          'bg-green-100 text-green-700'
                        }`}>{config.icon}</span>

                        {/* Title */}
                        <input
                          type="text"
                          value={comp.title}
                          onChange={(e) => updateComponent(idx, 'title', e.target.value)}
                          className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-blue-500 min-w-0"
                          placeholder="Component title"
                        />

                        {/* Points */}
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={comp.points || ''}
                            onChange={(e) => updateComponent(idx, 'points', Number(e.target.value) || 0)}
                            placeholder="0"
                            className="w-16 px-2 py-1.5 border border-gray-200 rounded text-sm text-right focus:outline-none focus:border-blue-500"
                          />
                          <span className="text-xs text-gray-500">pts</span>
                        </div>

                        {/* Remove */}
                        <button
                          type="button"
                          className="p-1 border-none bg-transparent cursor-pointer text-gray-400 hover:text-red-500 transition-colors duration-200"
                          onClick={() => removeComponent(idx)}
                          title="Remove component"
                        >
                          &times;
                        </button>
                      </div>

                      {/* ERD (Environment) scope selector */}
                      {comp.type === 'erd_env' && (
                        <div className="ml-8 flex items-center gap-2">
                          <span className="text-xs text-gray-500 whitespace-nowrap">Scope:</span>
                          <select
                            value={comp.environment_scope || 'all'}
                            onChange={(e) => updateComponent(idx, 'environment_scope', e.target.value)}
                            className="flex-1 px-2 py-1 border border-indigo-200 rounded text-xs bg-indigo-50 text-indigo-800 focus:outline-none focus:border-indigo-400"
                          >
                            <option value="all">All environments ({fullEnvironments.length} found)</option>
                            {fullEnvironments.map(env => (
                              <option key={env.environment_id} value={env.environment_id}>
                                {env.name || env.environment_id}
                              </option>
                            ))}
                          </select>
                          {comp.environment_scope === 'all' && fullEnvironments.length > 0 && (
                            <span className="text-xs text-indigo-600 whitespace-nowrap">
                              {fullEnvironments.map(e => e.name || e.environment_id).join(', ')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Points summary */}
                <div className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium ${
                  pointsMismatch ? 'bg-yellow-50 border border-yellow-200 text-yellow-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  <span>Component total: {componentPointsSum} pts</span>
                  <span>Assignment total: {totalPoints} pts</span>
                  {pointsMismatch && (
                    <span className="text-yellow-600">Points don't match</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Due Date and Points Row */}
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <label className="block mb-2 font-medium text-gray-800 text-sm" htmlFor="due_date">Due Date</label>
              <input
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-colors duration-200 focus:outline-none focus:border-blue-600"
                id="due_date"
                type="datetime-local"
                value={formData.due_date}
                onChange={(e) => handleChange('due_date', e.target.value)}
              />
            </div>

            <div>
              <label className="block mb-2 font-medium text-gray-800 text-sm" htmlFor="total_points">Total Points</label>
              <input
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-colors duration-200 focus:outline-none focus:border-blue-600"
                id="total_points"
                type="number"
                min="0"
                step="1"
                value={formData.total_points}
                onChange={(e) => handleChange('total_points', e.target.value)}
                required
              />
            </div>
          </div>

          {/* Due Date Behavior Settings */}
          <div className="mb-5 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h3 className="font-semibold text-gray-800 text-sm mb-3">Due Date Behavior</h3>

            {/* Lock After Due Date */}
            <div className="mb-3">
              <label className="flex items-center cursor-pointer font-normal">
                <input
                  className="w-auto mr-2 cursor-pointer"
                  type="checkbox"
                  checked={formData.lock_after_due_date}
                  onChange={(e) => handleChange('lock_after_due_date', e.target.checked)}
                />
                <span className="text-gray-700 text-sm font-medium">Lock assignment after due date</span>
              </label>
              <p className="text-xs text-gray-600 ml-6 mt-1">
                When enabled, students cannot submit after the due date passes
              </p>
            </div>

            {/* Allow Late Submissions */}
            {!formData.lock_after_due_date && (
              <div className="mb-3">
                <label className="flex items-center cursor-pointer font-normal">
                  <input
                    className="w-auto mr-2 cursor-pointer"
                    type="checkbox"
                    checked={formData.allow_late_submission}
                    onChange={(e) => handleChange('allow_late_submission', e.target.checked)}
                  />
                  <span className="text-gray-700 text-sm font-medium">Allow late submissions</span>
                </label>
                <p className="text-xs text-gray-600 ml-6 mt-1">
                  Submissions after due date will be marked as late
                </p>
              </div>
            )}

            {/* Late Submission Settings */}
            {!formData.lock_after_due_date && formData.allow_late_submission && (
              <div className="ml-6 p-3 bg-white border border-gray-200 rounded-md space-y-3">
                {/* Late Due Date */}
                <div>
                  <label className="block mb-1 font-medium text-gray-700 text-sm">Late Due Date (Optional)</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-600"
                    type="datetime-local"
                    value={formData.late_due_date}
                    onChange={(e) => handleChange('late_due_date', e.target.value)}
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Final deadline for late submissions. Leave empty for no limit.
                  </p>
                </div>

                {/* Late Penalty Type */}
                <div>
                  <label className="block mb-1 font-medium text-gray-700 text-sm">Penalty Type</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-600"
                    value={formData.late_penalty_type}
                    onChange={(e) => handleChange('late_penalty_type', e.target.value)}
                  >
                    <option value="percentage">Percentage Deduction</option>
                    <option value="points">Fixed Points Deduction</option>
                    <option value="flat">Flat Penalty (applied once)</option>
                  </select>
                </div>

                {/* Late Penalty Value */}
                <div>
                  <label className="block mb-1 font-medium text-gray-700 text-sm">
                    Penalty Amount ({formData.late_penalty_type === 'percentage' ? '%' : 'points'})
                  </label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-600"
                    type="number"
                    min="0"
                    step={formData.late_penalty_type === 'percentage' ? '1' : '0.1'}
                    max={formData.late_penalty_type === 'percentage' ? '100' : undefined}
                    value={formData.late_penalty_value}
                    onChange={(e) => handleChange('late_penalty_value', e.target.value)}
                  />
                </div>

                {/* Late Penalty Interval */}
                {formData.late_penalty_type !== 'flat' && (
                  <div>
                    <label className="block mb-1 font-medium text-gray-700 text-sm">Penalty Interval</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-600"
                      value={formData.late_penalty_interval}
                      onChange={(e) => handleChange('late_penalty_interval', e.target.value)}
                    >
                      <option value="hour">Per Hour Late</option>
                      <option value="day">Per Day Late</option>
                      <option value="week">Per Week Late</option>
                    </select>
                  </div>
                )}

                {/* Example */}
                <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                  <strong>Example:</strong> {
                    formData.late_penalty_type === 'flat'
                      ? `Any late submission receives a ${formData.late_penalty_value}${formData.late_penalty_type === 'percentage' ? '%' : ' point'} deduction`
                      : `${formData.late_penalty_value}${formData.late_penalty_type === 'percentage' ? '%' : ' points'} deducted per ${formData.late_penalty_interval} late`
                  }
                </div>
              </div>
            )}
          </div>

          {/* Publish Checkbox */}
          <div className="mb-5">
            <label className="flex items-center cursor-pointer font-normal">
              <input
                className="w-auto mr-2 cursor-pointer"
                type="checkbox"
                checked={formData.is_published}
                onChange={(e) => handleChange('is_published', e.target.checked)}
              />
              <span className="text-gray-600 text-sm">Publish immediately (make visible to students)</span>
            </label>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="p-3 bg-red-100 text-red-800 border border-red-200 rounded-md mb-5 text-sm">
              {error}
            </div>
          )}

          {/* Modal Actions */}
          <div className="flex justify-end gap-3 pt-5 border-t border-gray-200 mt-5">
            <button
              type="button"
              className={`px-5 py-2.5 border-none rounded-md text-sm font-medium transition-all duration-200 ${
                loading ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-60' : 'bg-gray-200 text-gray-600 cursor-pointer hover:bg-gray-300'
              }`}
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-5 py-2.5 border-none rounded-md text-sm font-medium transition-all duration-200 ${
                loading ? 'bg-blue-400 text-white cursor-not-allowed opacity-60' : 'bg-blue-600 text-white cursor-pointer hover:bg-blue-700'
              }`}
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Assignment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAssignment;
